/* WebComponentNewsletterSignup component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, h, Prop, State, Event, EventEmitter } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import NewsletterSignupTemplate from './templates/cwc-newsletter-signup.template';
import { AbstractComponent, Slot } from '@cewe-designsystem/abstract';
import { SignUpState } from './types/signup-state.enum';
import { FormState } from './types/form-state.enum';
import { ButtonState } from './types/button-state.enum';
import { UsageState } from './types/usage-state.enum';
import UtilNewsletterApi, { STATES, UtilNewsletterApiOptions } from '@cewe-designsystem/util_newsletter-api';

const EMAIL_INPUT_VALIDATION_DELAY = 1000;

interface NewsletterSignupEventEmitData {
  element: object;
  usage: string;
}

@Component({
  tag: 'cwc-newsletter-signup',
  styleUrl: './cwc-newsletter-signup.scss',
  shadow: true,
})
export class NewsletterSignupComponent {
  /**
   * email input validation delay in milliseconds
   */
  static EMAIL_INPUT_DELAY: number = EMAIL_INPUT_VALIDATION_DELAY;

  /**
   * element
   */
  @Element()
  element: HTMLElement;

  /**
   * slots
   */
  @Slot('initial-headline') initialHeadline;
  @Slot('initial-text') initialText;
  @Slot('initial-additionaltext') initialAdditionalText;
  @Slot('initial-subtext') initialSubtext;
  @Slot('successful-headline') successfulHeadline;
  @Slot('successful-text') successfulText;
  @Slot('successful-subtext') successfulSubtext;
  @Slot('successful-illustration') successfulIllustration;
  @Slot('failed-headline') failedHeadline;
  @Slot('failed-text') failedText;
  @Slot('failed-subtext') failedSubtext;
  @Slot('failed-illustration') failedIllustration;

  @Slot('illustration-before') illustrationBefore;
  @Slot('illustration-after') illustrationAfter;

  /**
   * key account id
   */
  @Prop() keyAccountId: string;

  /**
   * Endpoint URL for newsletter API
   */
  @Prop() newsletterApiEndpoint: string;

  /**
   * language
   */
  @Prop() language: string;

  /**
   * locale
   */
  @Prop() locale: string;

  /**
   * referrer
   */
  @Prop() referrer: string;

  /**
   * placeholder text for input element
   */
  @Prop() placeholderText: string;

  /**
   * form button text
   */
  @Prop() buttonCtaText: string;

  /**
   * form button retry text
   */
  @Prop() buttonRetryText: string;

  /**
   * form invalid text
   */
  @Prop() invalidFormText: string;

  /**
   * usage type on nl signup | possible["embedded"]
   */
  @Prop() usage: string;

  /**
   * state of the signup process
   */
  @State() signUpState: SignUpState;

  /**
   * state of the form
   */
  @State() formState: FormState = FormState.VALID;

  /**
   * state of the button
   */
  @State() buttonCtaState: ButtonState = ButtonState.DISABLED;

  /**
   * state of the usage type
   */
  @State() usageState: UsageState;

  /**
   * state of the graphic before visibility
   */
  @Prop() graphicBeforeInvisible: boolean;

  /**
   * state of the graphic after visibility
   */
  @Prop() graphicAfterInvisible: boolean;

  /**
   * email value of the input field
   */
  emailValue: string;

  /**
   * email input validation delay handler reference
   */
  emailInputDelayReference: number;

  /**
   * Event thrown if the newsletter-signup was successful
   *
   * @docs
   * @event on:newsletter-signup:successful
   */
  @Event({
    eventName: 'on:newsletter-signup:successful',
  })
  onNewsletterSignupSuccess: EventEmitter;

  /**
   * Event thrown if the newsletter-signup failed
   *
   * @docs
   * @event on:newsletter-signup:failure
   */
  @Event({
    eventName: 'on:newsletter-signup:failure',
  })
  onNewsletterSignupFailure: EventEmitter;

  /**
   * Event thrown if the newsletter-signup throws an error
   *
   * @docs
   * @event on:newsletter-signup:error
   */
  @Event({
    eventName: 'on:newsletter-signup:error',
  })
  onNewsletterSignupError: EventEmitter;

  /**
   * signUp
   */
  async signUp(event: Event) {
    event.preventDefault();

    this.signUpState = SignUpState.INITIAL;

    if (!this.isFormValid()) {
      return;
    }

    this.signUpState = SignUpState.LOAD;

    const properties: {
      locale?: string;
      language?: string;
      referrer: string;
      kid: string;
      email: string;
    } = {
      email: this.emailValue,
      kid: this.keyAccountId,
      referrer: window.location.href,
    };

    if (this.referrer) {
      properties.referrer = this.referrer;
    }

    if (this.language) {
      properties.language = this.language;
    }

    if (this.locale) {
      properties.locale = this.locale;
    }

    const newsletterApiOptions: UtilNewsletterApiOptions = {};
    if (this.newsletterApiEndpoint) {
      newsletterApiOptions.endpointUrl = this.newsletterApiEndpoint;
    }

    const newsletterApi = new UtilNewsletterApi(newsletterApiOptions),
      result = await newsletterApi.signIn(properties);

    if (result.state == STATES.ERROR) {
      this.signUpState = SignUpState.FAILED;
      /* emit the event - for tracking and things */
      this.emitError(this.eventEmitData());
      return;
    }

    if (result.state == STATES.FAILURE) {
      this.signUpState = SignUpState.INITIAL;
      this.formState = FormState.INVALID;
      this.buttonCtaState = ButtonState.DISABLED;
      /* emit the event - for tracking and things */
      this.emitFailure(this.eventEmitData());
      return;
    }

    this.signUpState = SignUpState.SUCCESSFUL;
    /* emit the event - for tracking and things */
    this.emitSuccess(this.eventEmitData());
  }

  /**
   * signUpRetry
   */
  signUpRetry() {
    this.signUpState = SignUpState.INITIAL;
  }

  /**
   * emailChanged
   * @param event
   */
  emailChanged(event: Event) {
    const emailInput = event.target as HTMLInputElement;
    this.emailValue = emailInput.value;
    this.formState = FormState.VALID;

    this.handleEmailInputDelay(() => {
      this.isFormValid();
    });
  }

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It is important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {}

  /**
   * Called every time the component is disconnected from the DOM, i.e., it calls be dispatched more than once
   * DO not confuse with a "onDestroy" kind of event.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec.
   */
  disconnectedCallback() {}

  /**
   * Called once just after the component is first connected to the DOM.
   * A promise can be returned, that can be used to wait for the first render.
   */
  componentWillLoad() {
    this.signUpState = SignUpState.INITIAL;
    this.usageState = this.usage === 'embedded' ? UsageState.EMBEDDED : UsageState.DEFAULT;
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {}

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {}

  /**
   * Called after every render().
   */
  componentDidRender() {}

  /**
   * Called when the component is about to be updated because some Prop() or State() changed.
   * It is never called during the first render().
   * A promise can be returned, that can be used to wait for the next render.
   */
  componentWillUpdate() {}

  /**
   * Called just after the component updates. It is never called during the first render().
   */
  componentDidUpdate() {}

  /**
   * render
   */
  render() {
    return <NewsletterSignupTemplate controller={this} />;
  }

  /**
   * EventEmitData
   */
  private eventEmitData(): NewsletterSignupEventEmitData {
    return {
      element: this.element,
      usage: this.usageState,
    };
  }

  /**
   * emitError (type safe)
   */
  private emitError(emitData: NewsletterSignupEventEmitData) {
    this.onNewsletterSignupError.emit(emitData);
  }

  /**
   * emitFailure (type safe)
   */
  private emitFailure(emitData: NewsletterSignupEventEmitData) {
    this.onNewsletterSignupFailure.emit(emitData);
  }

  /**
   * emitSuccess (type safe)
   */
  private emitSuccess(emitData: NewsletterSignupEventEmitData) {
    this.onNewsletterSignupSuccess.emit(emitData);
  }

  /**
   * isFormValid
   * @private
   */
  private isFormValid() {
    this.formState = FormState.VALID;
    this.buttonCtaState = ButtonState.ENABLED;

    const isEmailInputEmpty = this.isEmailInputEmpty(),
      isEmailInputValid = this.isEmailInputValid();

    if (!isEmailInputEmpty && isEmailInputValid) {
      return true;
    }

    if (!isEmailInputValid) {
      this.formState = FormState.INVALID;
    }

    this.buttonCtaState = ButtonState.DISABLED;

    return false;
  }

  /**
   * isEmailInputValid
   * @private
   */
  private isEmailInputValid(): boolean {
    const input = this.getEmailInput();
    if (input != null && input.checkValidity) {
      return input.checkValidity();
    }

    return true;
  }

  /**
   * isEmailInputEmpty
   * @private
   */
  private isEmailInputEmpty(): boolean {
    const input = this.getEmailInput();
    return input != null && input.value.length == 0;
  }

  /**
   * getEmailInput
   * @private
   */
  private getEmailInput() {
    return this.getContent().querySelector<HTMLInputElement>('input[name="email"]');
  }

  /**
   * handleEmailInputDelay
   * @param callback
   * @private
   */
  private handleEmailInputDelay(callback: TimerHandler) {
    if (this.emailInputDelayReference) {
      clearTimeout(this.emailInputDelayReference);
    }

    this.emailInputDelayReference = setTimeout(callback, NewsletterSignupComponent.EMAIL_INPUT_DELAY);
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(NewsletterSignupComponent.prototype, AbstractComponent.prototype);

export interface NewsletterSignupComponent extends AbstractComponent {}
